// Responses and rebuttals: a claim linked to the named person's response (what they said when asked, or in public)
// or to a claim from a source that contradicts it. Investigative reports print both next to the fact ("Contacted,
// X said…"; "Y disputes this"). A link joins two claims; it is stored apart from the claims (which stay append-only)
// and follows a claim to its newer version when the claim is edited.

import type { AttributionLevel, ReviewStatus } from './claims';
import type { ReliabilityStatus } from './reliability';
import type { ReportLanguage } from './report-language';

export const CLAIM_LINK_KINDS = ['response', 'contradiction'] as const;
export type ClaimLinkKind = (typeof CLAIM_LINK_KINDS)[number];
export const isClaimLinkKind = (v: unknown): v is ClaimLinkKind => CLAIM_LINK_KINDS.includes(v as ClaimLinkKind);

/**
 * How a link reads from each end. `out`: from the claim responded to or contradicted ("Response", "Contradicted
 * by"); `in`: from the response or the contradicting claim ("Responds to", "Contradicts").
 */
export const CLAIM_LINK_LABELS: Record<ReportLanguage, Record<ClaimLinkKind, { out: string; in: string }>> = {
  en: {
    response: { out: 'Response', in: 'Responds to' },
    contradiction: { out: 'Contradicted by', in: 'Contradicts' },
  },
  fr: {
    response: { out: 'Réponse', in: 'Répond à' },
    contradiction: { out: 'Contredit par', in: 'Contredit' },
  },
};

/** The claim at the other end of a link, in words. */
export interface LinkedClaim {
  claimId: string;
  entityId: string;
  entityName: string;
  /** "Field: value", or "relation Other entity". */
  text: string;
  status: ReviewStatus;
  /** Off-record ends are never printed in exports. */
  attributionLevel: AttributionLevel;
  reliability: ReliabilityStatus;
  /** Its first source in words, or null. */
  source: string | null;
}

export interface ClaimLink {
  id: string;
  kind: ClaimLinkKind;
  /** `out`: this claim is the one responded to / contradicted; `in`: this claim is the response / contradicting one. */
  direction: 'out' | 'in';
  other: LinkedClaim;
  note: string | null;
  createdAt: number;
  createdBy: string | null;
}

export interface ClaimLinkInput {
  otherClaimId: string;
  kind: ClaimLinkKind;
  note?: string | null;
}

/** A response or rebuttal as a full report's source note prints it, after the note's source. */
export interface NoteLink {
  kind: ClaimLinkKind;
  direction: 'out' | 'in';
  entityName: string;
  text: string;
  source: string | null;
}

/** "Add a response": what the named person said, recorded as a Statement on them and linked as the response. */
export interface ResponseInput {
  /** The actor who responded (usually the person the claim names). */
  respondentId: string;
  /** What they said or wrote, word for word. */
  text: string;
  /** When ("2025-03-12", "March 2025"). */
  date?: string | null;
  /** Where it was said or published (a cited source); none when the team was told directly. */
  source?: { publisherName?: string | null; title?: string | null; url?: string | null; publishedAt?: string | null } | null;
  note?: string | null;
}
